import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import './Header.css';
import BoschLogoSvg from '../../assets/bosch-logo.svg';
import BoschLogoBlackSvg from '../../assets/bosch-logo-black.svg';
import {
  Dropdown,
  Avatar,
  Layout,
  Flex,
  Typography,
  ConfigProvider,
} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ErrorMessageContext } from '../../context/ErrorMessageContext';
import { useTheme } from '../../context/ThemeContext';
import { DARK, LIGHT } from '../../utilities/constants';
import DayNightToggle from './DayNightToggle';
import {
  isLoginExpired,
  kebabToTitleCase,
  getErrorMessage,
} from '../../utilities/commonFunctions';
import { ReactComponent as OptionsSvg } from '../../assets/icons/options-horizontal.svg';
import Icon, {
  LeftOutlined,
  UserOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { ReactComponent as ChangePassword } from '../../assets/icons/change-password.svg';
import { ReactComponent as Legal } from '../../assets/icons/legal.svg';
import { ReactComponent as Dpn } from '../../assets/icons/dpn.svg';
import { ReactComponent as Tnc } from '../../assets/icons/tnc.svg';

const { Header } = Layout;
const { Title, Text } = Typography;

const MobileHeader = ({ heading, navigate }) => (
  <Flex gap={16} align="center">
    {heading && <LeftOutlined onClick={() => navigate(-1)} />}
    <Title level={5} style={{ margin: 0 }}>
      {kebabToTitleCase(heading) || 'RideBuddy Device Registration'}
    </Title>
  </Flex>
);

MobileHeader.propTypes = {
  heading: PropTypes.string,
  navigate: PropTypes.func.isRequired,
};

const pageTitles = {
  overview: 'Home',
  'vehicle-monitoring': 'Vehicle Monitoring',
  broadcast: 'Broadcast Channel',
  'device-management': 'Device Management',
  'driver-monitoring-system': 'Driver Monitoring System (DMS)',
  'safety-and-anomaly-monitoring': 'Safety and Anomaly Monitoring',
  'user-management': 'User Management',
  'executive-console': 'Executive Console',
  'history-and-reports': 'History and Reports',
  'trip-surveillance-and-analysis': 'Trip Surveillance and Analysis',
  'trip-scheduling-and-management': 'Trip Scheduling and Management',
  'super-admin-console': 'Super Admin Console',
  'platform-operations-console': 'Platform Operations Console',
};

const PageHeader = ({ heading = '' }) => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const isMobileView = location.pathname.split('/')[1] === 'mobile';
  const path = location.pathname.split('/')[1];

  let navigate = useNavigate();
  const { logout, isLoggedIn } = useAuth();
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [selectedLegalKey, setSelectedLegalKey] = React.useState('');

  const handleLogout = async () => {
    try {
      await logout();
      toggleTheme(LIGHT, { persist: false });
      navigate(isMobileView ? '/device-registration-login' : '/login');
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to logout! Please try again after sometime.'
        )
      );
    }
  };

  const handleChangePassword = () => {
    navigate('/change-password');
  };

  useEffect(() => {
    const checkAndLogout = () => {
      if (isLoggedIn && isLoginExpired()) {
        handleLogout();
      }
    };
    checkAndLogout();

    const intervalId = setInterval(
      () => {
        checkAndLogout();
      },
      1000 * 60 * 60
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndLogout();
      }
    };

    const handleFocus = () => {
      checkAndLogout();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isLoggedIn]);

  const getInitials = () => {
    const name = sessionStorage.getItem('name') || '';
    const [first = '', last = ''] = name.trim().split(' ');
    const initials = `${first.charAt(0).toUpperCase()}${last.charAt(0).toUpperCase()}`;
    return initials.trim();
  };

  const items = [
    {
      key: '0',
      label: (
        <Flex gap={8} align="center">
          <Avatar
            className="avatar"
            size={28}
            icon={!getInitials() && <UserOutlined />}
          >
            {getInitials()}
          </Avatar>
          <Text
            style={{ fontSize: '14px' }}
          >{`${sessionStorage.getItem('name')}`}</Text>
        </Flex>
      ),
      disabled: true,
    },
    {
      type: 'divider',
      style: { margin: '0px 12px' },
    },
    ...(!isMobileView
      ? [
          {
            key: '1',
            label: 'Change Password',
            icon: <Icon component={ChangePassword} />,
            onClick: handleChangePassword,
          },
        ]
      : []),
    {
      key: '2',
      label: 'Legal',
      icon: <Icon component={Legal} />,
      children: [
        {
          key: '2-1',
          label: (
            <a
              href="/documents/ridebuddy-dpn.pdf"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Data Protection Notification PDF (opens in new tab)"
            >
              Data Protection Notification
            </a>
          ),
          icon: <Icon component={Dpn} />,
          onClick: () => setSelectedLegalKey('2-1'),
        },
        {
          key: '2-2',
          label: (
            <a
              href="/documents/ridebuddy-tnc.pdf"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Terms and Conditions PDF (opens in new tab)"
            >
              Terms &amp; Conditions
            </a>
          ),
          icon: <Icon component={Tnc} />,
          onClick: () => setSelectedLegalKey('2-2'),
        },
      ],
    },
    {
      key: '3',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const headerWidth =
    isLoggedIn &&
    !isMobileView &&
    !location.pathname.includes('super-admin-console') &&
    !location.pathname.includes('platform-operations-console') &&
    !location.pathname.includes('change-password')
      ? 'calc(100% - 48px)'
      : '100%';

  return (
    <ConfigProvider
      theme={{
        components: {
          Dropdown: {
            colorBgElevated: 'var(--body-bg-color, #000000)',
          },
          Menu: {
            popupBg: 'var(--body-bg-color, #000000)',
            itemBg: 'var(--body-bg-color, #000000)',
            itemHeight: 48,
            itemHoverBg: 'var(--hover-bg-color, #43464A)',
            itemHoverColor: 'var(--primary-text-color, #FFFFFF)',
            itemSelectedBg: 'var(--selected-bg-color, #2E3033)',
            itemSelectedColor: 'var(--primary-text-color, #FFFFFF)',
          },
          Select: {
            selectorBg: 'var(--body-bg-color, #000000)',
          },
        },
      }}
    >
      <Header
        className="header"
        style={{
          width: headerWidth,
          backgroundColor: isMobileView
            ? '#FFFFFF'
            : 'var(--content-bg-color, #000000)',
          borderBottom:
            !isMobileView &&
            `1px solid ${theme === DARK ? '#71767C' : '#8A9097'}`,
        }}
      >
        <Flex gap={24} className="full-width" wrap="wrap" align="center">
          <div className="logo-ridebuddy">
            {isMobileView ? (
              <MobileHeader heading={heading} navigate={navigate} />
            ) : (
              <Flex gap={8} style={{ cursor: 'pointer' }} align="center">
                <Title level={5} style={{ marginBottom: 0, marginTop: 0 }}>
                  {pageTitles[path]}
                </Title>
              </Flex>
            )}
          </div>

          {isLoggedIn && (
            <Dropdown
              menu={{
                items,
                triggerSubMenuAction: 'click',
                selectedKeys: selectedLegalKey ? [selectedLegalKey] : [],
              }}
              trigger={['click']}
              placement="bottomRight"
              open={dropdownOpen}
              onOpenChange={open => {
                setDropdownOpen(open);
                if (!open) setSelectedLegalKey('');
              }}
            >
              <button
                type="button"
                className="header-dropdown-trigger"
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                onClick={e => e.preventDefault()}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDropdownOpen(open => !open);
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isMobileView ? (
                  <OptionsSvg />
                ) : (
                  <Avatar
                    className={`avatar avatar-header${dropdownOpen ? ' avatar-header-selected' : ''}`}
                    size={28}
                    icon={!getInitials() && <UserOutlined />}
                  >
                    {getInitials()}
                  </Avatar>
                )}
              </button>
            </Dropdown>
          )}
          {!isMobileView && (
            <img
              src={theme === DARK ? BoschLogoSvg : BoschLogoBlackSvg}
              alt="Bosch logo"
              className="logo-bosch"
            />
          )}
          {isLoggedIn && !isMobileView && <DayNightToggle />}
        </Flex>
      </Header>
    </ConfigProvider>
  );
};

PageHeader.propTypes = {
  heading: PropTypes.string,
};

export default React.memo(PageHeader);
